'use client';

import * as React from 'react';
import { TrendingDownIcon, TrendingUpIcon } from 'lucide-react';
import { motion } from 'motion/react';
import { Bar, BarChart, XAxis } from 'recharts';

import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle
} from '@workspace/ui/components/card';
import {
    ChartContainer,
    ChartTooltip,
    ChartTooltipContent
} from '@workspace/ui/components/chart';
import { cn } from '@workspace/ui/lib/utils';

const DATA = [
    { month: 'Jan', revenue: 4200, expenses: 3100 },
    { month: 'Feb', revenue: 3800, expenses: 3900 },
    { month: 'Mar', revenue: 5100, expenses: 2800 },
    { month: 'Apr', revenue: 4800, expenses: 3400 },
    { month: 'May', revenue: 6200, expenses: 4100 },
    { month: 'Jun', revenue: 7500, expenses: 3800 }
];

const MotionCard = motion.create(Card);

export function BentoProfitLossCard({
    className,
    ...props
}: React.ComponentPropsWithoutRef<typeof MotionCard>): React.JSX.Element {
    return (
        <MotionCard
            className={cn('relative overflow-hidden', className)}
            {...props}
        >
            <CardHeader className="px-4 sm:px-6">
                <CardTitle className="text-lg sm:text-xl">Gewinn & Verlust</CardTitle>
                <CardDescription className="text-xs sm:text-sm">Auswertung in Echtzeit</CardDescription>
            </CardHeader>
            <CardContent className="px-4 sm:px-6">
                <div className="mb-4 grid grid-cols-2 gap-2 sm:mb-6 sm:gap-4">
                    <div className="rounded-lg border p-2 shadow-sm sm:p-3">
                        <div className="mb-1 flex items-center gap-1.5 text-[10px] text-muted-foreground sm:mb-2 sm:gap-2 sm:text-xs">
                            <div className="rounded-md bg-primary/10 p-1 text-primary">
                                <TrendingUpIcon className="size-3" />
                            </div>
                            Einnahmen
                        </div>
                        <div className="text-base font-bold sm:text-xl">31.600 €</div>
                    </div>
                    <div className="rounded-lg border p-2 shadow-sm sm:p-3">
                        <div className="mb-1 flex items-center gap-1.5 text-[10px] text-muted-foreground sm:mb-2 sm:gap-2 sm:text-xs">
                            <div className="rounded-md bg-destructive/10 p-1 text-destructive">
                                <TrendingDownIcon className="size-3" />
                            </div>
                            Ausgaben
                        </div>
                        <div className="text-base font-bold sm:text-xl">21.100 €</div>
                    </div>
                </div>

                <ChartContainer
                    config={{
                        revenue: {
                            label: 'Einnahmen',
                            color: 'hsl(var(--chart-1))'
                        },
                        expenses: {
                            label: 'Ausgaben',
                            color: 'hsl(var(--chart-2))'
                        }
                    }}
                    className="h-[140px] w-full sm:h-[180px]"
                >
                    <BarChart data={DATA} margin={{ left: -20, right: 0 }}>
                        <XAxis
                            dataKey="month"
                            tickLine={false}
                            tickMargin={8}
                            axisLine={false}
                            fontSize={10}
                        />
                        <Bar
                            dataKey="revenue"
                            fill="var(--primary)"
                            radius={[4, 4, 0, 0]}
                            stackId="a"
                            maxBarSize={32}
                        />
                        <Bar
                            dataKey="expenses"
                            fill="var(--muted)"
                            radius={[4, 4, 0, 0]}
                            stackId="a"
                            maxBarSize={32}
                        />
                        <ChartTooltip
                            content={<ChartTooltipContent indicator="dashed" />}
                            cursor={false}
                        />
                    </BarChart>
                </ChartContainer>
            </CardContent>
        </MotionCard>
    );
}
